import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { isPainelOficinaRoute } from '@/lib/painelOficina';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { resolveRiqueRouteContext } from '@/lib/riqueContext';

export const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(Boolean(document.fullscreenElement));
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setPageContext = useRiqueChatStore((s) => s.setPageContext);
  const setCompanyId = useRiqueChatStore((s) => s.setCompanyId);
  const setPendingPrompt = useRiqueChatStore((s) => s.setPendingPrompt);

  const isPortalPage = location.pathname === '/portal';
  const isKanbanPage = location.pathname === '/kanban';
  const isOficinaPage = isPainelOficinaRoute(location.pathname);
  const isRiquePage = location.pathname === '/rique' || location.pathname.startsWith('/rique/');
  // Portal não usa full-bleed: o mesmo padrão aplica overflow-hidden e impede scroll vertical.
  const useFullBleedLayout = isKanbanPage || isOficinaPage || isRiquePage;
  const isOficinaPresentationMode = isOficinaPage && isFullscreen;
  // Portal e apresentação da oficina escondem o Header; Rique mantém a topbar (busca, sino, usuário).
  const hideAppHeader = isPortalPage || isOficinaPresentationMode;
  const hideAppChrome = isPortalPage || isOficinaPresentationMode;

  React.useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Legado: ?openRique=1 → módulo /rique
  useEffect(() => {
    if (searchParams.get('openRique') !== '1') return;
    const prompt = searchParams.get('prompt')?.trim();
    if (prompt) setPendingPrompt(prompt);
    const next = new URLSearchParams(searchParams);
    next.delete('openRique');
    next.delete('prompt');
    setSearchParams(next, { replace: true });
    const qs = prompt ? `?prompt=${encodeURIComponent(prompt)}` : '';
    navigate(`/rique${qs}`, { replace: true });
  }, [searchParams, setPendingPrompt, setSearchParams, navigate]);

  const screenScope = useRiqueChatStore((s) => s.screenScope);

  useEffect(() => {
    if (isRiquePage) return;
    const ctx = resolveRiqueRouteContext(location.pathname, location.search);
    const scope = useRiqueChatStore.getState().screenScope;
    let pageContext = ctx.pageContext;
    if (
      location.pathname.startsWith('/diretoria') &&
      scope.dataFrom &&
      scope.dataTo &&
      !pageContext.includes(scope.dataFrom)
    ) {
      pageContext += ` Período A aplicado na tela: ${scope.dataFrom} a ${scope.dataTo}. Período B: ${scope.compareFrom} a ${scope.compareTo}.`;
    }
    setPageContext(pageContext);
    useRiqueChatStore.getState().setRoutePath(location.pathname);
    if (ctx.companyIdFromUrl) setCompanyId(ctx.companyIdFromUrl);
  }, [location.pathname, location.search, screenScope, setPageContext, setCompanyId, isRiquePage]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'r') {
        event.preventDefault();
        navigate('/rique');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  return (
    <div className="h-screen min-h-0 overflow-hidden bg-background">
      {!hideAppChrome && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
        />
      )}

      <div
        className={`transition-all duration-300 flex h-full min-h-0 max-h-full flex-col overflow-hidden ${hideAppChrome ? '' : sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}
      >
        {!hideAppHeader && (
          <Header
            onMenuClick={() => setSidebarOpen(true)}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
          />
        )}

        <main
          className={
            useFullBleedLayout
              ? 'h-full min-h-0 flex-1 overflow-hidden'
              : isPortalPage
                ? 'min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-0'
                : 'min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-6 md:py-8'
          }
        >
          <div
            className={
              useFullBleedLayout
                ? 'flex h-full min-h-0 max-h-full flex-col'
                : isPortalPage
                  ? 'w-full min-w-0 flex flex-col'
                  : 'mx-auto w-full max-w-[1600px] pl-6 pr-6 sm:pl-8 sm:pr-10 lg:pl-10 lg:pr-14 xl:pl-12 xl:pr-20'
            }
          >
            <Outlet />
          </div>
        </main>

        {/** Portal e telas com chrome exibem rodapé; oculta em oficina em modo apresentação (hideAppChrome e não é portal). shrink-0 mantém a barra fixa ao fundo do viewport. */}
        {!useFullBleedLayout && (isPortalPage || !hideAppChrome) && (
          <div className="shrink-0">
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};
